import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type IngredientPromotion } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the banner text shown when the customization machine auto-promotes
 * one or more ingredients from extras to included. Returns `undefined` when
 * there is nothing to announce so callers can skip the banner.
 */
export const useIngredientsSwappedBannerMessage = () => {
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getIngredientFragement = useCallback(
    (promotion: IngredientPromotion) => {
      const { ingredientName, includedQuantity, totalQuantity } = promotion;

      if (includedQuantity >= totalQuantity) {
        return ingredientName;
      }

      if (includedQuantity === 1) {
        return formatMessage(messages.ingredientFragmentSingular, { name: ingredientName });
      }

      return formatMessage(messages.ingredientFragmentPlural, {
        count: includedQuantity,
        name: ingredientName,
      });
    },
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(
    (promotions: IngredientPromotion[]) => {
      if (promotions.length === 0) {
        return;
      }

      if (promotions.length >= 3) {
        return formatMessage(messages.ingredientsSwappedMany, { count: promotions.length });
      }

      const fragments = promotions.map(getIngredientFragement);

      if (promotions.length === 2) {
        return formatMessage(messages.ingredientsSwappedTwo, {
          first: fragments[0],
          second: fragments[1],
        });
      }

      return formatMessage(messages.ingredientsSwappedOne, { fragment: fragments[0] });
    },
    [formatMessage, getIngredientFragement],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  ingredientsSwappedOne: {
    defaultMessage: "We've made {fragment} included. Your order is priced at its best.",
    description:
      'Customization | Banner shown when one ingredient is auto-promoted from extras to included',
  },
  ingredientsSwappedTwo: {
    defaultMessage: "We've made {first} and {second} included. Your order is priced at its best.",
    description:
      'Customization | Banner shown when two ingredients are auto-promoted from extras to included',
  },
  ingredientsSwappedMany: {
    defaultMessage: "We've made {count} ingredients included. Your order is priced at its best.",
    description:
      'Customization | Banner shown when three or more ingredients are auto-promoted from extras to included',
  },
  ingredientFragmentSingular: {
    defaultMessage: '1 portion of {name}',
    description: 'Customization | Fragment describing an ingredient with one promoted portion',
  },
  ingredientFragmentPlural: {
    defaultMessage: '{count} portions of {name}',
    description:
      'Customization | Fragment describing an ingredient with multiple promoted portions',
  },
});
